import { useState, useCallback, useRef, useEffect } from "react";
import { GetAlergyIntoleranceByOrganization } from "../getAlergyIntoleranceByOrganization";
import { extractPatients } from "../../condition/hooks/useConditionByOrganization";

interface UseAlergyIntoleranceByOrganizationProps {
  /** Organization id sem prefixo, ex: 05490544000100 */
  organizationId: string;
  /** Status (ex: active, completed) */
  status?: string | string[];
  /** Data inicial YYYY-MM-DD (opcional) */
  startDate?: string;
  /** Data final YYYY-MM-DD (opcional) */
  endDate?: string;
  /** Quantidade de registros (default 100) */
  count?: number;
  /** Incluir Encounter no bundle */
  includeEncounter?: boolean;
  /** Indica se deve retornar apenas o resumo (opcional) */
  summary?: boolean;
  /** Incluir Patient no bundle */
  includePatient?: boolean;
}

interface UseAlergyIntoleranceByOrganizationResult {
  data: {
    allergyIntolerances: fhir5.AllergyIntolerance[];
    total?: number;
    patients?: fhir5.Patient[];
  };
  loading: boolean;
  error: Error | null;
  fetchAlergyIntolerances: () => Promise<void>;
}

function extractAlergyIntolerances(
  bundle?: fhir5.Bundle
): fhir5.AllergyIntolerance[] {
  if (!bundle?.entry) return [];
  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.AllergyIntolerance> =>
        entry.resource?.resourceType === "AllergyIntolerance"
    )
    .map((entry) => entry.resource!)
    .filter(
      (resource): resource is fhir5.AllergyIntolerance => resource !== undefined
    );
}


export function useAlergyIntoleranceByOrganization(
  props: UseAlergyIntoleranceByOrganizationProps
): UseAlergyIntoleranceByOrganizationResult {
  const [data, setData] = useState<{
    allergyIntolerances: fhir5.AllergyIntolerance[];
    patients?: fhir5.Patient[];
    total?: number;
  }>({
    allergyIntolerances: [],
    patients: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const paramsRef = useRef(props);

  useEffect(() => {
    paramsRef.current = props;
  }, [props]);

  const fetchAlergyIntolerances = useCallback(async () => {
    const currentParams = paramsRef.current;
    if (!currentParams.organizationId) return;
    try {
      setLoading(true);
      setError(null);
      
      // Primeiro busca o count total
      const countResponse = await GetAlergyIntoleranceByOrganization({
        ...currentParams,
        summary: true,
        count: 0,
      });
      
      const totalCount = countResponse.total || 0;
      console.log(`Total de AllergyIntolerance encontrados: ${totalCount}`);
      
      // Agora busca todos os registros com o count adequado
      const response = await GetAlergyIntoleranceByOrganization({
        ...currentParams,
        count: totalCount > 0 ? totalCount : 1000, // Usa o total encontrado ou um valor padrão alto
        summary: false
      });
      
      const allergyIntolerances = extractAlergyIntolerances(response);
      const patients = extractPatients(response);
      setData({ allergyIntolerances, patients, total: totalCount });
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Erro ao buscar AllergyIntolerance")
      );
      console.error("useAlergyIntoleranceByOrganization error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchAlergyIntolerances };
}
